import { Box } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TravelReferenceDocumentsCard } from '@/module/Travel/ReferenceDocument/components/TravelReferenceDocumentsCard';
import { isTravelAdminRole } from '@/module/Travel/travelRoles';

import { NoContentPlaceholder } from '@/core/components';

import { useCurrentUser } from '@feature/security';
import { useAppNotification } from '@feature/utils';

import { TravelSettingsForm } from './TravelSettingsForm';
import { TravelSettingsStaleAlert } from './components/TravelSettingsStaleAlert';
import {
  getTravelSettingsOptions,
  isTravelSettingsStaleError,
  useTravelSettingsUpdate,
} from './model/TravelSettingsQuery';
import {
  TravelSettingsSchema,
  toTravelSettingsRequest,
  toTravelSettingsSchema,
  travelSettingsSchema,
} from './model/TravelSettingsSchema';
import { TravelSettings } from './model/TravelSettingsTypes';

export function TravelSettingsEdit() {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();

  if (!isTravelAdminRole(currentRole)) {
    return <NoContentPlaceholder title={t('travel.settings.noAccess')} />;
  }

  return <TravelSettingsEditContent />;
}

function TravelSettingsEditContent() {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { data, refetch, isRefetching } = useSuspenseQuery(getTravelSettingsOptions());
  const { mutate, isPending, isError, isSuccess } = useTravelSettingsUpdate();
  // A background refetch must neither wipe typed values nor make a stale form saveable.
  const [loaded, setLoaded] = useState({ settings: data, revision: 0 });
  const [isRefused, setIsRefused] = useState(false);

  const resetTo = (settings: TravelSettings) => {
    setLoaded(previous => ({ settings, revision: previous.revision + 1 }));
    setIsRefused(false);
  };

  const onSubmit = (values: TravelSettingsSchema) =>
    mutate(toTravelSettingsRequest(values, loaded.settings.version), {
      onError: error => {
        if (isTravelSettingsStaleError(error)) {
          setIsRefused(true);
          return;
        }
        notification.error(error);
      },
      onSuccess: saved => {
        resetTo(saved);
        notification.success();
      },
    });

  const onReload = () =>
    refetch().then(result => {
      if (result.isSuccess) {
        resetTo(result.data);
      } else if (result.isError) {
        notification.error(result.error);
      }
    });

  const values = useMemo(() => toTravelSettingsSchema(loaded.settings), [loaded.settings]);

  return (
    <>
      <TravelSettingsForm
        // Reloaded settings that equal the ones first loaded would not reset the form, so every reset remounts it.
        key={loaded.revision}
        title={t('travel.settings.title')}
        notice={
          isRefused && (
            <TravelSettingsStaleAlert
              isReloading={isRefetching}
              onReload={onReload}
            />
          )
        }
        schema={travelSettingsSchema}
        defaultValues={{
          leadTimeUnit: 'CALENDAR_DAYS',
          erasmusLeadTimeUnit: 'CALENDAR_DAYS',
          parallelEnforcement: 'BLOCK',
          erasmusParallelEnforcement: 'BLOCK',
          leaveDaysEnforcement: 'BLOCK',
          dayCapEnforcement: 'BLOCK',
          researchSecretariatVerification: true,
          internationalSecretariatVerification: true,
        }}
        values={values}
        isSubmitting={isPending}
        isError={isError}
        isSuccess={isSuccess}
        onSubmit={onSubmit}
        onCancel={undefined}
      />
      <Box
        px="xl"
        pb="xl"
      >
        <TravelReferenceDocumentsCard />
      </Box>
    </>
  );
}
